import { darkTheme } from '../../../lib/tokens';
import type { PipelineStage } from '../data/types';

interface StageCardProps {
  stage: PipelineStage;
  large?: boolean;
}

export function StageCard({ stage, large = false }: StageCardProps): JSX.Element {
  return (
    <article
      id={`stufe-${stage.id}`}
      className="scroll-mt-6 rounded-md border p-4 lg:p-5"
      style={{ background: darkTheme.colors.bgSurface, borderColor: darkTheme.colors.borderSubtle }}
    >
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span
          className="rounded-sm px-2 py-0.5 text-[11px] font-bold uppercase tracking-widest"
          style={{ background: darkTheme.colors.accentSubtle, color: darkTheme.colors.accentLight }}
        >
          Stufe {stage.nr}
        </span>
        <h3 className={`font-semibold text-text-primary ${large ? 'text-2xl' : 'text-base'}`}>{stage.title}</h3>
      </header>

      <p className={`mt-2 text-text-secondary ${large ? 'text-lg' : 'text-sm'}`}>{stage.purpose}</p>

      <dl className={`mt-4 grid gap-x-6 gap-y-2 sm:grid-cols-3 ${large ? 'text-base' : 'text-xs'}`}>
        <Field label="Eingang" value={stage.input} large={large} />
        <Field label="Ergebnis" value={stage.output} large={large} mono />
        <Field label="Werkzeug" value={stage.tool} large={large} mono />
      </dl>

      <div className={`mt-4 space-y-3 ${large ? 'text-lg leading-relaxed' : 'text-sm leading-relaxed'}`}>
        {stage.detail.map((p) => (
          <p key={p.slice(0, 40)} className="text-text-secondary">
            {p}
          </p>
        ))}
      </div>

      {(stage.risk || stage.cost) && (
        <footer className="mt-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          {stage.cost && (
            <Tag label="Kosten" value={stage.cost} color={darkTheme.colors.info} bg={darkTheme.colors.infoBg} large={large} />
          )}
          {stage.risk && (
            <Tag label="Falle" value={stage.risk} color={darkTheme.colors.warning} bg={darkTheme.colors.warningBg} large={large} />
          )}
        </footer>
      )}
    </article>
  );
}

function Field({
  label,
  value,
  large,
  mono,
}: {
  label: string;
  value: string;
  large: boolean;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className={`text-text-tertiary uppercase tracking-wider ${large ? 'text-xs' : 'text-[10px]'}`}>{label}</dt>
      <dd className={`mt-0.5 text-text-primary ${mono ? 'break-all font-mono' : 'break-words'}`}>{value}</dd>
    </div>
  );
}

function Tag({
  label,
  value,
  color,
  bg,
  large,
}: {
  label: string;
  value: string;
  color: string;
  bg: string;
  large: boolean;
}): JSX.Element {
  return (
    <p
      className={`rounded-sm px-3 py-2 ${large ? 'text-base' : 'text-xs'}`}
      style={{ background: bg, color: darkTheme.colors.textSecondary }}
    >
      <span className="font-bold uppercase tracking-wider" style={{ color }}>
        {label}
      </span>{' '}
      {value}
    </p>
  );
}
